import React, { useState, useEffect, useRef } from 'react';
import api from "../../../../api";
import { API_BASE } from '../../../../api';
import { ENDPOINTS } from '../../Utils/Endpoints'; // Fix imports relative to path
import { getSessionUserId } from '../../../../utils/SessionUser'; // Fix imports relative to path
import { toISODateTime, parseDateTimeFlexible } from '../../../../utils/DateHelpers';

const ForoCompletoModal = ({ foro, onClose, usuariosMap = {}, nombreUsuarioActual = "Colaborador" }) => {
    // Local state for comments, similar to ForosTematicos
    const [comentarios, setComentarios] = useState(foro.comentarios || []);
    const [nuevoComentario, setNuevoComentario] = useState("");
    const [erroresComentario, setErroresComentario] = useState("");
    const chatEndRef = useRef(null);

    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [comentarios]);

    const [currentUsersMap, setCurrentUsersMap] = useState({});
    const [currentPhotosMap, setCurrentPhotosMap] = useState({});
    const [currentUserName, setCurrentUserName] = useState(nombreUsuarioActual);
    const [currentUserPhoto, setCurrentUserPhoto] = useState(null);

    // Load users unconditionally to get photos
    useEffect(() => {
        const loadUsers = async () => {
            try {
                const [resUsuarios, resPersonas] = await Promise.all([
                    api.get(ENDPOINTS.usuarios),
                    api.get(ENDPOINTS.personas)
                ]);
                const personasMap = {};
                const personasFotoMap = {};
                resPersonas.data.forEach(p => {
                    const idP = String(p.idPersona || p.IdPersona || 0);
                    personasMap[idP] = `${p.nombre || p.Nombre || ''} ${p.apellidoPaterno || p.ApellidoPaterno || ''} ${p.apellidoMaterno || p.ApellidoMaterno || ''}`.trim();
                    personasFotoMap[idP] = p.fotoPerfil || p.FotoPerfil || null;
                });
                const map = {};
                const fotoMap = {};
                resUsuarios.data.forEach(u => {
                    const idPers = String(u.idPersona || u.IdPersona || 0);
                    const idUsr = String(u.idUsuario || u.IdUsuario || 0);
                    if (idPers !== "0") {
                        if (personasMap[idPers]) map[idUsr] = personasMap[idPers];
                        if (personasFotoMap[idPers]) fotoMap[idUsr] = personasFotoMap[idPers];
                    }
                });
                setCurrentUsersMap(map);
                setCurrentPhotosMap(fotoMap);

                const idCurrent = String(getSessionUserId());
                if (map[idCurrent]) {
                    setCurrentUserName(map[idCurrent]);
                }
                if (fotoMap[idCurrent]) {
                    setCurrentUserPhoto(fotoMap[idCurrent]);
                }
            } catch (e) {
                console.error("Error loading users for modal", e);
            }
        };
        loadUsers();
    }, []);

    // Load comments on mount
    useEffect(() => {
        const loadComments = async () => {
            if (!foro.id) return;
            try {
                const res = await api.get(`${ENDPOINTS.forosRespuestas}?idForo=${foro.id}`);
                const filteredData = res.data.filter(r => String(r.idForo || r.IdForo) === String(foro.id));
                const mapped = filteredData.map(r => ({
                    id: r.idRespuesta || r.IdRespuesta,
                    autor: currentUsersMap[r.idUsuario || r.IdUsuario] || r.autor || r.Autor || r.nombreUsuario || "Colaborador",
                    mensaje: r.mensaje || r.Mensaje,
                    fecha: r.fechaCreacion || r.FechaCreacion,
                    idUsuario: r.idUsuario || r.IdUsuario
                }));
                // Ordenar más antiguos primero (formato chat)
                mapped.sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
                setComentarios(mapped);
            } catch (e) {
                console.error("Error loading comments", e);
            }
        };
        loadComments();
    }, [foro.id, currentUsersMap]);


    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleAgregarComentario = async (e) => {
        if (e) e.preventDefault();

        if (!nuevoComentario.trim()) {
            setErroresComentario("Ingrese un comentario.");
            return;
        }

        if (isSubmitting) return;
        setIsSubmitting(true);

        const idUsuario = getSessionUserId();
        // Calcular fecha para asegurar que vaya al final (resolver timestamps futuros)
        const now = new Date();
        const currentComments = comentarios || [];
        let fechaFinal = now;

        if (currentComments.length > 0) {
            const lastComment = currentComments[currentComments.length - 1];
            const lastDate = new Date(lastComment.fecha);
            if (!isNaN(lastDate.getTime()) && lastDate >= now) {
                fechaFinal = new Date(lastDate.getTime() + 1000); // Sumar 1 segundo al último
            }
        }

        const payload = {
            idRespuesta: 0,
            idForo: foro.id,
            mensaje: nuevoComentario.trim(),
            idUsuario: idUsuario,
            autor: currentUserName,
            fechaCreacion: toISODateTime(fechaFinal)
        };

        try {
            await api.post(ENDPOINTS.forosRespuestas, payload);
            setNuevoComentario("");
            setErroresComentario("");

            // Reload comments
            const res = await api.get(`${ENDPOINTS.forosRespuestas}?idForo=${foro.id}`);
            const filteredData = res.data.filter(r => String(r.idForo || r.IdForo) === String(foro.id));
            const mapped = filteredData.map(r => ({
                id: r.idRespuesta || r.IdRespuesta,
                autor: currentUsersMap[r.idUsuario || r.IdUsuario] || r.autor || r.Autor || r.nombreUsuario || "Colaborador",
                mensaje: r.mensaje || r.Mensaje,
                fecha: r.fechaCreacion || r.FechaCreacion,
                idUsuario: r.idUsuario || r.IdUsuario
            }));
            // Ordenar más antiguos primero (formato chat)
            mapped.sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
            setComentarios(mapped);

        } catch (error) {
            console.error("Error posting comment", error);
            setErroresComentario("No se pudo publicar el comentario. Inténtalo de nuevo.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const getImageUrl = (path) => {
        if (!path) return null;
        if (path.startsWith("http") || path.startsWith("data:")) return path;
        // Simple heuristic for server url if needed, but usually passed full or relative
        // Assuming path is relative to server root if not http
        return `${API_BASE.replace(/\/api\/?$/, '')}${path.startsWith('/') ? '' : '/'}${path}`;
    };

    // Placeholder logic same as dashboard
    const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='250' viewBox='0 0 400 250'%3E%3Crect fill='%23E5E7EB' width='400' height='250'/%3E%3Cpath fill='%239CA3AF' d='M163 142.5l24-24 24 24 48-48 48 48V187H163v-44.5zm48-39.5c-8.3 0-15-6.7-15-15s6.7-15 15-15 15 6.7 15 15-6.7 15-15 15z'/%3E%3C/svg%3E";


    const [activeTab, setActiveTab] = useState('info'); // 'info' or 'comments'

    return (
        <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 1000, padding: '2rem'
        }}>
            <div className="intra-card" style={{
                width: '95%', maxWidth: '1000px', height: '85vh', maxHeight: '90vh',
                display: 'flex', flexDirection: 'column',
                backgroundColor: 'white', borderRadius: '12px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', overflow: 'hidden'
            }}>
                {/* Modal Header */}
                <div style={{
                    padding: '1.25rem 2rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    borderBottom: '1px solid #f1f5f9'
                }}>
                    <h3 style={{ 
                        margin: 0, 
                        fontSize: '1.25rem', 
                        color: 'var(--color-primary)', 
                        fontWeight: 800,
                        letterSpacing: '-0.02em'
                    }}>
                        Foro Temático
                    </h3>
                    <button 
                        onClick={onClose} 
                        style={{ 
                            background: '#f1f5f9', 
                            border: 'none', 
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.2rem', 
                            cursor: 'pointer', 
                            color: '#64748b',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e2e8f0'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                    >
                        ×
                    </button>
                </div>

                {/* Navigation Tabs */}
                <div style={{
                    display: 'flex',
                    borderBottom: '1px solid #e2e8f0',
                    backgroundColor: 'white',
                }}>
                    <button
                        onClick={() => setActiveTab('info')}
                        style={{
                            flex: 1,
                            padding: '1.25rem 1rem',
                            border: 'none',
                            borderBottom: `3px solid ${activeTab === 'info' ? 'var(--color-primary)' : 'transparent'}`,
                            background: 'none',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            color: activeTab === 'info' ? 'var(--color-primary)' : '#64748b',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            outline: 'none',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}
                    >
                        Información
                    </button>
                    <button
                        onClick={() => setActiveTab('comments')}
                        style={{
                            flex: 1,
                            padding: '1.25rem 1rem',
                            border: 'none',
                            borderBottom: `3px solid ${activeTab === 'comments' ? 'var(--color-primary)' : 'transparent'}`,
                            background: 'none',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            color: activeTab === 'comments' ? 'var(--color-primary)' : '#64748b',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            outline: 'none',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}
                    >
                        Comentarios ({comentarios.length})
                    </button>
                </div>

                {/* Content Area */}
                <div style={{
                    display: 'flex',
                    flex: 1,
                    overflow: 'hidden',
                    backgroundColor: activeTab === 'comments' ? '#f8fafc' : 'white'
                }}>
                    {activeTab === 'info' ? (
                        <div style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: '2.5rem',
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            <div style={{ maxWidth: '900px', width: '100%' }}>
                                {/* Image */}
                                <div style={{
                                    width: '100%', height: 'auto', maxHeight: '450px', backgroundColor: '#f1f5f9',
                                    borderRadius: '16px', overflow: 'hidden', marginBottom: '2rem',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                                }}>
                                    {foro.imagen ? (
                                        <img
                                            src={getImageUrl(foro.imagen)}
                                            alt={foro.titulo}
                                            style={{ width: '100%', height: 'auto', maxHeight: '450px', objectFit: 'cover' }}
                                            onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
                                        />
                                    ) : (
                                        <div style={{ fontSize: '4rem', color: '#cbd5e1' }}>💬</div>
                                    )}
                                </div>

                                {/* Meta */}
                                <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                                    <span className="intra-chip" style={{
                                        padding: '0.5rem 1rem',
                                        borderRadius: '12px',
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        backgroundColor: '#e0e7ff',
                                        color: '#3730a3',
                                    }}>
                                        {foro.area || "General"}
                                    </span>
                                    {foro.fechaCreacion && (
                                        <span style={{ fontSize: '0.875rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <strong>📅 Publicado:</strong> {new Date(foro.fechaCreacion).toLocaleString('es-MX', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase()}
                                        </span>
                                    )}
                                </div>

                                <h1 style={{ fontSize: '2.25rem', fontWeight: '800', color: '#1e293b', marginBottom: '1.5rem', lineHeight: 1.2 }}>
                                    {foro.titulo}
                                </h1>

                                <div style={{
                                    fontSize: '1.125rem',
                                    color: '#334155',
                                    lineHeight: 1.8,
                                    whiteSpace: 'pre-wrap',
                                    padding: '1.5rem',
                                    backgroundColor: '#f8fafc',
                                    borderRadius: '12px',
                                    borderLeft: '4px solid #cbd5e1'
                                }}>
                                    {foro.descripcionInicial || foro.descripcion}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            maxWidth: '1000px',
                            margin: '0 auto',
                            width: '100%',
                            position: 'relative'
                        }}>
                            {/* Comments Title Slim */}
                            <div style={{
                                padding: '1rem 2rem',
                                background: 'white',
                                borderBottom: '1px solid #e2e8f0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>
                                    Conversación
                                </h4>
                                <span style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: 600 }}>
                                    {comentarios.length} mensajes
                                </span>
                            </div>

                            {/* Comments scroll area */}
                            <div style={{ flex: 1, overflowY: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                {comentarios.length > 0 ? comentarios.map((comentario, index) => {
                                    const initials = comentario.autor ? comentario.autor.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : "U";
                                    const bgColors = ['#FCA5A5', '#FCD34D', '#34D399', '#60A5FA', '#818CF8', '#A78BFA', '#F472B6'];
                                    const avatarColor = bgColors[index % bgColors.length];

                                    const isOwner = Number(comentario.idUsuario) === Number(foro.creadoPor);
                                    const userFoto = currentPhotosMap[String(comentario.idUsuario)];

                                    return (
                                        <div key={comentario.id || index} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                                            <div style={{ width: '32px', height: '32px', minWidth: '32px', borderRadius: '50%', backgroundColor: (userFoto && userFoto !== "") ? 'transparent' : (isOwner ? '#0f172a' : avatarColor), color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 'bold', marginTop: '2px', border: isOwner && (!userFoto || userFoto === "") ? '2px solid #e2e8f0' : 'none', overflow: 'hidden' }}>
                                                {(userFoto && userFoto !== "") ? (
                                                    <img src={getImageUrl(userFoto)} alt={comentario.autor} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex'; }} />
                                                ) : null}
                                                <div style={{ display: (userFoto && userFoto !== "") ? 'none' : 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                                                    {isOwner ? '👑' : initials}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.1rem' }}>
                                                    <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: isOwner ? '#0f172a' : '#334155' }}>
                                                        {comentario.autor}
                                                        {isOwner && (
                                                            <span style={{ marginLeft: '6px', fontSize: '0.65rem', backgroundColor: '#f1f5f9', color: '#475569', padding: '1px 6px', borderRadius: '8px', border: '1px solid #e2e8f0', verticalAlign: 'middle' }}>AUTOR</span>
                                                        )}
                                                    </span>
                                                    <span style={{ fontSize: '0.7rem', color: '#94a3b8', marginLeft: 'auto', textAlign: 'right' }}>
                                                        {comentario.fecha ? new Date(comentario.fecha).toLocaleString('es-MX', {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            hour12: true
                                                        }).toLowerCase() : ''}
                                                    </span>
                                                </div>
                                                <p style={{ margin: 0, color: '#475569', fontSize: '0.9rem', lineHeight: '1.4', wordBreak: 'break-word', backgroundColor: isOwner ? '#f8fafc' : 'transparent', padding: isOwner ? '6px 10px' : '0', borderRadius: isOwner ? '8px' : '0', border: isOwner ? '1px solid #f1f5f9' : 'none' }}>
                                                    {comentario.mensaje}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                }) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', gap: '0.5rem', textAlign: 'center', padding: '2rem 1rem' }}>
                                        <span style={{ fontSize: '2rem' }}>💬</span>
                                        <p style={{ margin: 0 }}>Aún no hay respuestas.</p>
                                        <p style={{ fontSize: '0.8rem', margin: 0 }}>Sé el primero en contestar.</p>
                                    </div>
                                )}
                                <div ref={chatEndRef} />
                            </div>

                            {/* Input - Fixed at bottom of tab */}
                            <div style={{
                                padding: '1.25rem 2rem',
                                borderTop: '1px solid #e2e8f0',
                                background: 'white',
                                boxShadow: '0 -4px 10px rgba(0,0,0,0.03)'
                            }}>
                                {erroresComentario && <p className="intra-error-text" style={{ fontSize: '0.75rem', marginBottom: '0.5rem' }}>{erroresComentario}</p>}
                                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                                    <div style={{ width: '36px', height: '36px', minWidth: '36px', borderRadius: '50%', backgroundColor: currentUserPhoto ? 'transparent' : 'var(--color-primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '6px', overflow: 'hidden', border: '2px solid #f1f5f9' }}>
                                        {currentUserPhoto ? (
                                            <img src={getImageUrl(currentUserPhoto)} alt={currentUserName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex'; }} />
                                        ) : null}
                                        <div style={{ display: currentUserPhoto ? 'none' : 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                                            {currentUserName ? currentUserName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : "YO"}
                                        </div>
                                    </div>
                                    <div style={{ flex: 1, backgroundColor: '#f1f5f9', borderRadius: '24px', padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center', border: '1px solid #e2e8f0' }}>
                                        <textarea
                                            value={nuevoComentario}
                                            onChange={(e) => setNuevoComentario(e.target.value)}
                                            placeholder="Escribe un comentario..."
                                            rows={1}
                                            style={{ width: '100%', border: 'none', background: 'transparent', outline: 'none', resize: 'none', fontSize: '1rem', padding: 0, fontFamily: 'inherit', color: '#334155' }}
                                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAgregarComentario(e); } }}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleAgregarComentario}
                                        disabled={!nuevoComentario.trim() || isSubmitting}
                                        style={{
                                            backgroundColor: (!nuevoComentario.trim() || isSubmitting) ? '#e2e8f0' : 'var(--color-primary)',
                                            border: 'none',
                                            borderRadius: '50%',
                                            width: '44px',
                                            height: '44px',
                                            minWidth: '44px',
                                            cursor: (!nuevoComentario.trim() || isSubmitting) ? 'default' : 'pointer',
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.2s ease',
                                            opacity: isSubmitting ? 0.7 : 1,
                                            boxShadow: (!nuevoComentario.trim() || isSubmitting) ? 'none' : '0 4px 6px rgba(0,0,0,0.1)'
                                        }}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="22" y1="2" x2="11" y2="13"></line>
                                            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ForoCompletoModal;
